import { CropYear, FieldViewModel, IOmniaShell, isDate, ISoilAnalysis, ITab, numericTextSort, OmniaObjectType, param, selectMany, SoilP2O5Method } from "../../omnia"
import { ISoilService } from "../../types"
import { getActivity } from "../activity"
import { AnalysisEditCtrl } from "../edit"
import { getInterpretations, InterpGroup } from "./interpretation"
import { validateNutrients, validateSoil, validateSoilProp } from "./validation"

export interface IMainTab extends ITab {
    showAllocations: boolean
}

export interface ISoilAllocTarget {
    field: FieldViewModel;
    zoneId?: number;
}

export class SoilAnalysisEditCtrl extends AnalysisEditCtrl<ISoilAnalysis> {

    static createPopup(): ng.IDirective {
        return super.create(SoilAnalysisEditCtrl);
    };

    private _soilAnalysis: ISoilAnalysis;

    protected p2O5Methods: any[];

    private soilErrors: { [key: string]: boolean };
    private soilPropErrors: { [key: string]: boolean };
    private nutrientsErrors: { [key: string]: boolean };

    public userDefinedYear: boolean;

    public currentMainTab: IMainTab;
    public mainTabs: IMainTab[];

    public currentTab: ITab;
    public tabs: ITab[];

    public selectedSoilAllocTargets: ISoilAllocTarget[];
    public deletedSoilAllocTargets: ISoilAllocTarget[];

    private interpretations: InterpGroup[];

    static $inject = ["$scope", "OmniaSoilService", "OmniaShell"];

    constructor($scope: ng.IScope, svc: ISoilService, shell: IOmniaShell) {

        super($scope, svc, shell);

        this.p2O5Methods = param.SoilP2O5Method()

        this.setupTabs();

        super.setup();

    }

    private setupTabs() {

        this.mainTabs = [

            { key: "analysis", text: "Analysis", showAllocations: true },
            { key: "interpretations", text: "Interpretations", showAllocations: false }
        ];

        this.currentMainTab = this.mainTabs[0];


        this.tabs = [

            { key: "analysis-popup", text: "Standard analysis" },
            { key: "soilprop", text: "Soil properties" },
            { key: "micronutrients", text: "Additional elements" },
            { key: "total-elements", text: "Elements as KGs" }
        ];

        this.currentTab = this.tabs[0];

    }

    get hideIdTitle(): boolean {
        return true;
    }

    protected getTypeInfo() {
        return { name: "Soil analysis", title: "soil analysis", type: OmniaObjectType.SoilAnalysis }
    }

    protected getPopupTemplate(): string {
        return "app/soil/edit/soil/soil.html";
    }

    protected setupAnalysis(a: ISoilAnalysis) {

        this._soilAnalysis = a;

        this.allocTargets = this.svc.fields
        this.allocTargets = numericTextSort(this.allocTargets, t => t.description)

        var allocations = !!this._soilAnalysis && this._soilAnalysis.Allocations ?
            this._soilAnalysis.Allocations.filter(a => (a.ZoneAlloc && !!a.ZoneId) || (!a.ZoneAlloc && !a.ZoneId)) :
            [];

        this.selectedSoilAllocTargets = !!this._soilAnalysis && allocations.length
            ? allocations.map(a => <ISoilAllocTarget>{
                field: this.allocTargets.find(f => f.fieldId === a.FieldId),
                zoneId: a.ZoneId
            })
            : this.cmd && this.cmd.alloc ? this.allocTargets.filter(f => f.fieldId === this.cmd.alloc.fieldId).map(f => <ISoilAllocTarget>{ field: f }) : [];

        this.deletedSoilAllocTargets = [];

        if (a) {

            a.P2O5Method = a.P2O5Method || SoilP2O5Method.Olsen;

        }

        this.interpretations = getInterpretations(this.svc, this._soilAnalysis)
        this.userDefinedYear = !this.newAnalysis;

        if (this.newAnalysis && (!a.FieldReference) && this.selectedSoilAllocTargets.length) {
            a.FieldReference = this.selectedSoilAllocTargets[0].field.fieldName;
        }

    }

    protected getActivity() {
        const a = this._soilAnalysis
        return getActivity(this.newAnalysis, { type: OmniaObjectType.SoilAnalysis, id: a?.SoilAnalysisId, number: a?.AnalysisNumber })
    }

    selectField(field: FieldViewModel) {
        if (field) {
            this.currentAllocSelection = null;

            if (!this.selectedSoilAllocTargets.find(a => a.field.fieldId === field.fieldId && !a.zoneId) &&
                !this.deletedSoilAllocTargets.find(a => a.field.fieldId === field.fieldId && !a.zoneId)) {
                this.selectedSoilAllocTargets.push({ field: field });
            }
        }

        if (this.newAnalysis && field && (!this._soilAnalysis.FieldReference)) {
            this._soilAnalysis.FieldReference = field.fieldName;
        }
    }

    unselectTarget(field: ISoilAllocTarget) {
        if (field && this.selectedSoilAllocTargets.indexOf(field) !== -1) {

            if (this.analysis.Allocations && this.analysis.Allocations.filter(a => a.FieldId === field.field.fieldId && !a.ZoneAlloc).length > 0) {
                if (this.deletedSoilAllocTargets.indexOf(field) === -1) {
                    this.deletedSoilAllocTargets.push(field);
                }
            }

            this.selectedSoilAllocTargets = this.selectedSoilAllocTargets.filter(f => field !== f);
        }
    }

    recoverTarget(field: ISoilAllocTarget) {
        if (field && this.deletedSoilAllocTargets.indexOf(field) !== -1) {
            this.deletedSoilAllocTargets = this.deletedSoilAllocTargets.filter(f => field !== f);

            if (this.selectedSoilAllocTargets.indexOf(field) === -1) {
                this.selectedSoilAllocTargets.push(field);
            }
        }
    }

    protected validate() {

        if (!this.validateSoil()) return false;
        if (!this.validateNutrients()) return false;
        if (!this.validateSoilProp()) return false;

        return true;

    }

    private validateSoil() {
        return !(this.soilErrors = validateSoil(this._soilAnalysis))
    }

    private validateSoilProp() {
        return !(this.soilPropErrors = validateSoilProp(this._soilAnalysis))
    }

    private validateNutrients() {
        return !(this.nutrientsErrors = validateNutrients(this._soilAnalysis))
    }

    protected updateOnDateChanged() {

        let currentYear = this._soilAnalysis.FirstYear;
        if (this.userDefinedYear && currentYear) return;

        let date = this.analysis.SamplingDate;

        if (isDate(date)) {
            this._soilAnalysis.FirstYear = CropYear.getYearFromDate(date);
        }

    }

    protected saveAnalysis(): ng.IPromise<number> {

        this._soilAnalysis.IsValid = true;

        let allocFieldsIds = this.selectedSoilAllocTargets && this.selectedSoilAllocTargets.length > 0
            ? this.selectedSoilAllocTargets.filter(a => !a.zoneId).map(f => f.field.fieldId)
            : null;

        this._soilAnalysis.Interpretations = selectMany(this.interpretations.map(i => i.Properties.map(p => p.getUpdateModel()))).filter(i => !!i)

        // Rounding
        if (this._soilAnalysis.SoilHealthIndex) {
            this._soilAnalysis.SoilHealthIndex = Math.round(this._soilAnalysis.SoilHealthIndex * 10) / 10;
        }

        if (this._soilAnalysis.CO2BurstIndex) {
            this._soilAnalysis.CO2BurstIndex = Math.round(this._soilAnalysis.CO2BurstIndex * 10) / 10;
        }

        if (this._soilAnalysis.CO2Burst) {
            this._soilAnalysis.CO2Burst = Math.round(this._soilAnalysis.CO2Burst);
        }

        const fieldIds = this.newAnalysis && this.allocTarget ? [this.allocTarget.fieldId] : allocFieldsIds

        return this.svc
            .saveSoilAnalysis(this._soilAnalysis, fieldIds, undefined)
            .then(a => a ? a.SoilAnalysisId : null)

    }

    protected deleteAnalysis(deleteOtherGridSamples: boolean) {

        let id = this._soilAnalysis.SoilAnalysisId;
        if (!id) return null;

        return this.svc.deleteSoilAnalysis(id, deleteOtherGridSamples);
    }

    get showInterpretation(): boolean {
        return !!this._soilAnalysis;
    }

}
